import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  User, FileText, HandCoins, LogOut, ArrowLeft, Mail, Phone,
  MapPin, Pencil, Save, Package, DollarSign, Clock,
} from "lucide-react";
import BottomNav from "@/components/BottomNav";
import type { User as SupaUser } from "@supabase/supabase-js";

const Account = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState<SupaUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [profileForm, setProfileForm] = useState({
    full_name: "", phone: "", address: "", city: "", state: "", zip: "",
  });

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
      if (!session?.user) navigate("/signin");
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
      if (!session?.user) navigate("/signin");
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  const { data: profile, refetch: refetchProfile } = useQuery({
    queryKey: ["profile", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase.from("profiles").select("*").eq("id", user!.id).single();
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (profile) {
      setProfileForm({
        full_name: profile.full_name || "",
        phone: profile.phone || "",
        address: profile.address || "",
        city: profile.city || "",
        state: profile.state || "",
        zip: profile.zip || "",
      });
    }
  }, [profile]);

  const { data: invoices } = useQuery({
    queryKey: ["invoices", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customer_invoices")
        .select("*, customer_invoice_items(*)")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: tradeins } = useQuery({
    queryKey: ["tradeins", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customer_tradeins")
        .select("*")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const handleSaveProfile = async () => {
    if (!user) return;
    const { error } = await supabase.from("profiles").update({
      full_name: profileForm.full_name.trim(),
      phone: profileForm.phone.trim(),
      address: profileForm.address.trim(),
      city: profileForm.city.trim(),
      state: profileForm.state.trim(),
      zip: profileForm.zip.trim(),
    }).eq("id", user.id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Profile updated" });
      setEditing(false);
      refetchProfile();
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="font-mono text-sm text-muted-foreground">Loading...</p>
      </div>
    );
  }

  const statusColor = (status: string) => {
    switch (status) {
      case "completed": return "default";
      case "pending": return "secondary";
      case "accepted": return "default";
      case "rejected": return "destructive";
      default: return "secondary";
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="max-w-lg mx-auto px-4 py-6 space-y-5">
        {/* Header */}
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="w-8 h-8 rounded-lg bg-card border border-border flex items-center justify-center hover:border-primary/40 transition-colors">
              <ArrowLeft className="w-4 h-4 text-foreground" />
            </button>
            <div>
              <h1 className="text-lg font-mono font-bold text-foreground tracking-tight">
                MY <span className="text-primary">ACCOUNT</span>
              </h1>
              <p className="text-[10px] font-mono text-muted-foreground truncate max-w-[200px]">{user?.email}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="font-mono text-xs gap-1" onClick={handleSignOut}>
            <LogOut className="w-3 h-3" /> Sign Out
          </Button>
        </header>

        <div className="h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

        {/* Tabs */}
        <Tabs defaultValue="profile" className="space-y-4">
          <TabsList className="w-full bg-card border border-border p-1 rounded-xl">
            <TabsTrigger value="profile" className="flex-1 font-mono text-[10px] tracking-wider gap-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-lg">
              <User className="w-3.5 h-3.5" /> Profile
            </TabsTrigger>
            <TabsTrigger value="purchases" className="flex-1 font-mono text-[10px] tracking-wider gap-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-lg">
              <FileText className="w-3.5 h-3.5" /> Purchases
            </TabsTrigger>
            <TabsTrigger value="tradeins" className="flex-1 font-mono text-[10px] tracking-wider gap-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-lg">
              <HandCoins className="w-3.5 h-3.5" /> Sold
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-mono text-xs font-bold text-foreground tracking-wider uppercase">Your Details</h2>
                <Button variant="ghost" size="sm" className="font-mono text-xs gap-1" onClick={() => editing ? handleSaveProfile() : setEditing(true)}>
                  {editing ? <><Save className="w-3 h-3" /> Save</> : <><Pencil className="w-3 h-3" /> Edit</>}
                </Button>
              </div>

              <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-mono text-muted-foreground flex items-center gap-1"><User className="w-3 h-3" /> Full Name</label>
                  {editing ? (
                    <Input value={profileForm.full_name} onChange={(e) => setProfileForm(p => ({ ...p, full_name: e.target.value }))} className="font-mono text-sm bg-background border-border" maxLength={100} />
                  ) : (
                    <p className="font-mono text-sm text-foreground">{profile?.full_name || "—"}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-mono text-muted-foreground flex items-center gap-1"><Mail className="w-3 h-3" /> Email</label>
                  <p className="font-mono text-sm text-foreground">{user?.email}</p>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-mono text-muted-foreground flex items-center gap-1"><Phone className="w-3 h-3" /> Phone</label>
                  {editing ? (
                    <Input value={profileForm.phone} onChange={(e) => setProfileForm(p => ({ ...p, phone: e.target.value }))} className="font-mono text-sm bg-background border-border" maxLength={20} />
                  ) : (
                    <p className="font-mono text-sm text-foreground">{profile?.phone || "—"}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-mono text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" /> Address</label>
                  {editing ? (
                    <div className="space-y-2">
                      <Input placeholder="Street" value={profileForm.address} onChange={(e) => setProfileForm(p => ({ ...p, address: e.target.value }))} className="font-mono text-sm bg-background border-border" maxLength={200} />
                      <div className="grid grid-cols-3 gap-2">
                        <Input placeholder="City" value={profileForm.city} onChange={(e) => setProfileForm(p => ({ ...p, city: e.target.value }))} className="font-mono text-sm bg-background border-border" maxLength={100} />
                        <Input placeholder="State" value={profileForm.state} onChange={(e) => setProfileForm(p => ({ ...p, state: e.target.value }))} className="font-mono text-sm bg-background border-border" maxLength={2} />
                        <Input placeholder="ZIP" value={profileForm.zip} onChange={(e) => setProfileForm(p => ({ ...p, zip: e.target.value }))} className="font-mono text-sm bg-background border-border" maxLength={10} />
                      </div>
                    </div>
                  ) : (
                    <p className="font-mono text-sm text-foreground">
                      {[profile?.address, profile?.city, profile?.state, profile?.zip].filter(Boolean).join(", ") || "—"}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Purchases Tab */}
          <TabsContent value="purchases">
            <div className="space-y-3">
              <h2 className="font-mono text-xs font-bold text-foreground tracking-wider uppercase">Purchase History</h2>
              {!invoices?.length ? (
                <div className="rounded-xl border border-border bg-card p-8 text-center">
                  <FileText className="w-8 h-8 text-muted-foreground/20 mx-auto mb-2" />
                  <p className="font-mono text-xs text-muted-foreground">No purchases yet</p>
                </div>
              ) : (
                invoices.map((inv) => (
                  <div key={inv.id} className="rounded-xl border border-border bg-card p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-mono text-xs font-bold text-foreground">{inv.invoice_number}</p>
                        <p className="font-mono text-[10px] text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(inv.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-sm font-bold text-primary">${Number(inv.total).toFixed(2)}</p>
                        <Badge variant={statusColor(inv.status) as any} className="text-[9px] font-mono">
                          {inv.status.toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                    {inv.customer_invoice_items?.length > 0 && (
                      <div className="border-t border-border pt-2 space-y-1">
                        {inv.customer_invoice_items.map((item: any) => (
                          <div key={item.id} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Package className="w-3 h-3 text-muted-foreground" />
                              <span className="font-mono text-[11px] text-foreground">{item.product_name}</span>
                              <span className="font-mono text-[10px] text-muted-foreground">×{item.quantity}</span>
                            </div>
                            <span className="font-mono text-[11px] text-primary">${Number(item.price).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Trade-ins Tab */}
          <TabsContent value="tradeins">
            <div className="space-y-3">
              <h2 className="font-mono text-xs font-bold text-foreground tracking-wider uppercase">Items Sold to Us</h2>
              {!tradeins?.length ? (
                <div className="rounded-xl border border-border bg-card p-8 text-center">
                  <HandCoins className="w-8 h-8 text-muted-foreground/20 mx-auto mb-2" />
                  <p className="font-mono text-xs text-muted-foreground">No trade-ins yet</p>
                </div>
              ) : (
                tradeins.map((ti) => (
                  <div key={ti.id} className="rounded-xl border border-border bg-card p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-mono text-xs font-bold text-foreground">{ti.item_name}</p>
                        <p className="font-mono text-[10px] text-muted-foreground">
                          {ti.tradein_number} • {ti.platform} • {ti.condition?.toUpperCase()}
                        </p>
                        <p className="font-mono text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5">
                          <Clock className="w-3 h-3" />
                          {new Date(ti.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-sm font-bold text-primary flex items-center gap-1">
                          <DollarSign className="w-3.5 h-3.5" />
                          {Number(ti.offer_amount).toFixed(2)}
                        </p>
                        <Badge variant={statusColor(ti.status) as any} className="text-[9px] font-mono">
                          {ti.status.toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <BottomNav />
    </div>
  );
};

export default Account;
